import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { Stack, Text } from '@solvhealth/jigsaw-v1';
import { isMobile, useUserAgent } from '~/core/util/device';
import { colors } from '~/constants/colors';
import { breakpoints } from '~/core/util/styledComponents';

const SeparatorLine = styled.hr.withConfig({ componentId: 'sc-zttk84-0' })`
  border: 4px solid ${colors.greyCorona};
  margin: 0 0;
  left: -16px;

  width: calc(100% + 24px);

  /** On mobile the separator touches both edges of the screen so we need to compensate by
   the 8px border on each side to prevent overflow */
  @media (max-width: ${breakpoints.md}) {
    width: calc(100% + 24px);
  }

  position: relative;
`;

type Props = {
  heading?: string;
  headingWrapper?: React.FC;
  subHeading?: string | ReactNode;
  children?: ReactNode;
  isSubsection?: boolean;
  noSeparatorBottom?: boolean;
  noPaddingTop?: boolean;
};

/**
 * Reusable components in CDP for displaying each main sections or sub sections
 *
 * @param props.isSubsection true for those subsections inside main sections in CDP (like office photos section, which is inside Location and Hours)
 * @param props.noSeparatorBottom true for components that does not need separator line and margin space at bottom
 * @param props.noPaddingTop true for components that do not need padding at the top
 * @returns a react component
 */
const CDPSection = (props: Props) => {
  const {
    heading,
    headingWrapper,
    subHeading,
    children,
    isSubsection,
    noSeparatorBottom,
    noPaddingTop,
  } = props;
  const userAgent = useUserAgent();

  const HeadingWrapper = headingWrapper ?? React.Fragment;

  return (
    <>
      <Stack
        paddingBottom={isSubsection || noSeparatorBottom ? 0 : 4}
        paddingTop={isSubsection || noPaddingTop ? 0 : 4}
        position="relative"
      >
        {(heading || subHeading) && (
          <HeadingWrapper>
            <Stack marginBottom={children ? 3 : 0} space={isSubsection ? 1 : 3}>
              {heading && <Text variant={isSubsection ? 'h4' : 'h3'}>{heading}</Text>}
              {subHeading && <Text variant={isSubsection ? 'body2' : 'body'}>{subHeading}</Text>}
            </Stack>
          </HeadingWrapper>
        )}
        {children}
      </Stack>
      {isMobile(userAgent) && !noSeparatorBottom && !isSubsection && <SeparatorLine />}
    </>
  );
};

export default CDPSection;
